import {Component, DestroyRef, EventEmitter, Inject, inject, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {IAusbildung} from '../../../shared/model/ausbildung.model';
import {BeraterService} from '../../../services/berater.service';
import {Berater} from '../../../shared/model/berater.model';
import {MatButton, MatIconButton, MatMiniFabButton} from '@angular/material/button';
import {MatError, MatFormField, MatLabel, MatSuffix} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatCheckbox} from '@angular/material/checkbox';
import {
  MatDatepicker,
  MatDatepickerActions,
  MatDatepickerApply,
  MatDatepickerCancel,
  MatDatepickerInput,
  MatDatepickerToggle
} from '@angular/material/datepicker';
import {TranslatePipe} from '@ngx-translate/core';
import {MatCard, MatCardContent} from '@angular/material/card';
import {MatIcon} from '@angular/material/icon';
import {MessageService} from '../../../shared/components/message.service';
import {DateService} from '../../../services/date.service';
import {ModalComponent} from '../../../shared/components/modal/modal.component';
import {ModalService} from '../../../shared/components/modal/modal.service';
import {InputUtils} from '../../../../utils/input-utils.component';
import {INPUT_LIMITS} from '../../../shared/constants/input-limits';
import {FieldWarningComponent} from '../../../shared/components/field-warning/field-warning.component';
import {ValidationFinding, findEntryFinding} from '../../../shared/model/validation-finding';

@Component({
  selector: 'app-education',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    ReactiveFormsModule,
    MatButton,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatCheckbox,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatDatepickerActions,
    MatDatepickerCancel,
    MatDatepickerApply,
    MatSuffix,
    TranslatePipe,
    MatCard,
    MatIcon,
    MatMiniFabButton,
    MatCardContent,
    ModalComponent,
    MatIconButton,
    FieldWarningComponent
  ],
  templateUrl: './education.component.html',
  styleUrl: './education.component.scss',
})
export class EducationComponent implements OnInit, OnChanges {
  @Input() ausbildung: IAusbildung[] = [];
  @Input() exportMode: boolean;
  @Input() twoPagerMode: boolean = false;
  @Input() berater: Berater;
  @Input() forcedSelectedIds: string[] | null = null;
  @Input() findings: ValidationFinding[] = [];

  @Output() exportSelectionChange = new EventEmitter<string[]>();
  // NP20-828: Nach Speichern reicht die Komponente den vom BE zurueckgelieferten
  // Berater direkt an den Parent hoch — der uebernimmt ihn immutable in
  // selectedBerater und triggert damit neue Badge-Berechnung, ohne Refetch.
  @Output() saved = new EventEmitter<Berater>();

  private readonly destroyRef = inject(DestroyRef);

  selectedExportIds: string[] = [];
  editMode: boolean = false;
  /** NP20-822: Laeuft gerade ein Speichern, loest ein weiterer Klick keinen zweiten Request aus. */
  isSaving = false;
  stillWorking: boolean = false;
  form: FormGroup;
  trainingCopy: IAusbildung[] = [];
  originalEducation: IAusbildung[] = [];
  formChanged = false;
  educationCopy: IAusbildung[] = [];
  showModal = false;
  currentIdx: number | null = null;
  protected get LIMITS() { return INPUT_LIMITS; }

  constructor(
    private fb: FormBuilder,
    private beraterService: BeraterService,
    private messageService: MessageService,
    private dateService: DateService,
    private modalService: ModalService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.locale = locale;
  }

  get educationArray(): FormArray {
    return this.form.get('ausbildung') as FormArray;
  }

  ngOnInit() {
    // Form + valueChanges genau einmal aufsetzen — vorher wurde beides in
    // ngOnChanges neu gebaut, sodass sich pro Input-Update eine zusaetzliche
    // valueChanges-Subscription ansammelte (NP20-828 Reviewer-Hinweis).
    // Falls ngOnChanges das Formular bereits angelegt hat (Test-Setup-Reihenfolge),
    // nicht ueberschreiben.
    if (!this.form) {
      this.form = this.fb.group({ausbildung: this.fb.array([])});
      if (this.ausbildung?.length > 0) {
        this.ausbildung.forEach(z => this.addEducationForm(z));
      }
      this.trainingCopy = JSON.parse(JSON.stringify(this.ausbildung ?? []));
    }

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formChanged = this.hasChanges());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['ausbildung'] && changes['ausbildung'].currentValue) {
      // Wenn ngOnChanges vor ngOnInit feuert (Test-Setup mit direktem Property-Set),
      // muss die Form hier lazy angelegt werden — sonst wirft educationArray.
      if (!this.form) {
        this.form = this.fb.group({ausbildung: this.fb.array([])});
      }
      // Waehrend der Nutzer bearbeitet niemals das FormArray aus einem externen
      // Input-Update ueberschreiben — sonst verliert er seine Eingaben, wenn der
      // Parent den Berater z.B. nach einem Speichern der Persoenlichen Daten
      // neu setzt.
      if (!this.editMode) {
        this.trainingCopy = JSON.parse(JSON.stringify(changes['ausbildung'].currentValue));
        this.educationArray.clear();
        if (this.ausbildung?.length > 0) {
          this.ausbildung.forEach(z => this.addEducationForm(z));
        }
      }
    }

    if (changes['exportMode'] || changes['twoPagerMode']) {
      if (this.exportMode && !this.twoPagerMode) {
        this.autoSelectAll();
      } else {
        this.selectedExportIds = [];
      }
    } else if (changes['ausbildung'] && this.exportMode && !this.twoPagerMode) {
      this.autoSelectAll();
    }

    if (changes['forcedSelectedIds'] && this.forcedSelectedIds !== null) {
      this.selectedExportIds = [...this.forcedSelectedIds];
      this.exportSelectionChange.emit([...this.selectedExportIds]);
    }
  }

  private autoSelectAll(): void {
    this.selectedExportIds = (this.ausbildung ?? []).map(a => a.uuid).filter((uuid): uuid is string => !!uuid);
    this.exportSelectionChange.emit([...this.selectedExportIds]);
  }

  toggleExportSelection(idx: string, checked: boolean): void {
    if (checked) {
      if (!this.selectedExportIds.includes(idx)) this.selectedExportIds.push(idx);
    } else {
      this.selectedExportIds = this.selectedExportIds.filter(i => i !== idx);
    }
    this.exportSelectionChange.emit([...this.selectedExportIds]);
  }

  onToggleEdit(): void {
    if (!this.editMode) {
      this.editMode = true;
      this.originalEducation = JSON.parse(JSON.stringify(this.ausbildung));
      this.educationCopy = JSON.parse(
        JSON.stringify(this.form.value.ausbildung)
      );

      this.formChanged = false;

      this.educationArray.clear();
      if (this.ausbildung?.length > 0) {
        this.ausbildung.forEach((a) => this.addEducationForm(a));
      } else {
        this.addEducationForm();
      }
    } else {
      this.save();
    }
  }

  onCancelEdit(): void {
    this.educationArray.clear();
    this.originalEducation.forEach((a) => this.addEducationForm(a));
    this.editMode = false;
    this.formChanged = false;
  }

  addEducationForm(data?: IAusbildung) {
    const group = this.fb.group({
      uuid: [data?.uuid || null],
      beginn: [data?.beginn || '', Validators.required],
      ausbildungseinrichtung: [
        data?.ausbildungseinrichtung || '',
        [Validators.required, Validators.maxLength(INPUT_LIMITS.SHORT_LABEL)]
      ],
      ausbildungsgang: [data?.ausbildungsgang || '', [Validators.required, Validators.maxLength(INPUT_LIMITS.SHORT_LABEL)]],
      abschluss: [data?.abschluss || '', [Validators.required, Validators.maxLength(INPUT_LIMITS.SHORT_LABEL)]],
      ort: [data?.ort || '', [Validators.required, Validators.maxLength(INPUT_LIMITS.LOCATION)]],
      ende: [data?.ende || ''],
      stillWorking: [!data?.ende],
      abschlussErreicht: [data?.abschlussErreicht || '']
    });

    const updateEndeValidation = (isWorking: boolean) => {
      const endeControl = group.get('ende');
      if (isWorking) {
        endeControl!.clearValidators();
        endeControl!.setValue('');
      } else {
        endeControl!.setValidators([Validators.required]);
      }
      endeControl!.updateValueAndValidity();
    };

    group.get('stillWorking')!.valueChanges.subscribe(updateEndeValidation);
    updateEndeValidation(group.get('stillWorking')!.value);

    this.educationArray.push(group);
  }

  save() {
    if (this.isSaving) return;
    this.ausbildung = this.educationArray.value;
    this.ausbildung = this.ausbildung.map((item) => ({
      ...item,
      beginn: this.dateService.toLocalDateString(item.beginn),
      ende: this.dateService.toLocalDateString(item.ende)
    }));
    // NP20-915: editMode erst nach der Antwort schliessen, sonst sieht ein Fehlschlag wie Erfolg aus.
    this.isSaving = true;
    this.beraterService
      .updateAusbildung(this.ausbildung, this.berater.id)
      .subscribe({
        next: (value) => {
          this.isSaving = false;
          this.editMode = false;
          this.berater = value;
          this.ausbildung = this.berater.ausbildung;
          this.trainingCopy = JSON.parse(JSON.stringify(this.ausbildung));
          this.formChanged = false;
          this.messageService.success('education.successMessage');
          this.saved.emit(value);
        },
        error: () => {
          this.isSaving = false;
          // Bearbeitungsmodus bleibt offen, die Eingaben stehen weiter im Formular.
          this.messageService.error('education.errorMessage');
        },
      });
  }

  // NP20-916: Loeschen nur vormerken — Speichern schickt es mit, Abbrechen nimmt es zurueck.
  // Vorher lief sofort ein PUT und der Abbrechen-Knopf wurde gesperrt.
  delete(index: number) {
    this.educationArray.removeAt(index);
    this.formChanged = this.hasChanges();
    this.messageService.info('education.infoDeleteMessage');
  }

  hasChanges(): boolean {
    return (
      JSON.stringify(this.form.value.ausbildung) !==
      JSON.stringify(this.educationCopy)
    );
  }

  openDeleteModal(idx: number) {
    this.currentIdx = idx;
    this.modalService.open('deleteEducation');
  }

  onKeyPress(event: KeyboardEvent) {
    InputUtils.allowLettersOnly(event);
  }

  entryFinding(entryIndex: number, field: string): ValidationFinding | null {
    if (this.editMode || this.exportMode) return null;
    return findEntryFinding(this.findings, entryIndex, field);
  }
}
